// client/src/components/doctors/dashboard/DoctorDashboard.jsx - COMPLETE WITH STATUS GUARD INTEGRATION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureControl } from '../../hooks/useFeatureControl';
import { useTenant } from '../../context/TenantContext';
import FeatureWrapper from '../common/FeatureWrapper';

import neurolexLogo from '../../assets/images/Neurolex_Logo_New.png';
import searchIcon from '../../assets/icons/indicators/search_icon.svg';
import closeIcon from '../../assets/icons/indicators/search_icon.svg';
import profileIcon from '../../assets/icons/indicators/complete_icon.svg';
import restrictedIcon from '../../assets/logo/doc_status.svg';


// ✅ NEW: Import DoctorStatusGuard components
import DoctorStatusGuard, { StatusBanner, RestrictedFeatureMessage } from '../common/DoctorStatusGuard';
import '../../styles/components/doctor/DoctorStatus.css';

import doctorService from '../../services/doctorService';
import '../../styles/components/doctor/DoctorDashboard.css';
import NotificationButton from '../common/NotificationButton';
import notificationService from '../../services/notificationService';

import { debounce } from 'lodash'; // Make sure lodash is available

const DoctorDashboard = () => {
  // ✅ Feature control and tenant context
  const featureControl = useFeatureControl();
  const { currentTenant, getThemeStyles, platformName } = useTenant();
  const theme = getThemeStyles();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ patients: [], doctors: [], totalResults: 0 });
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const toggleSearch = () => {
  setSearchOpen(!searchOpen);
  if (searchOpen) {
    setSearchQuery('');
    setSearchResults({ patients: [], doctors: [], totalResults: 0 });
    setShowSearchResults(false);
  }
};

const performSearch = debounce(async (query) => {
  if (!query || query.trim().length < 2) {
    setSearchResults({ patients: [], doctors: [], totalResults: 0 });
    setShowSearchResults(false);
    setSearchLoading(false);
    return;
  }

  try {
    setSearchLoading(true);
    console.log('🔍 Performing search for:', query);
    
    const response = await doctorService.performGlobalSearch(query.trim());
    
    if (response.success) {
      setSearchResults(response.data);
      setShowSearchResults(true);
      console.log('✅ Search completed:', response.data);
    } else {
      setSearchResults({ patients: [], doctors: [], totalResults: 0 });
      setShowSearchResults(false);
    }
  } catch (error) {
    console.error('❌ Search error:', error);
    setSearchResults({ patients: [], doctors: [], totalResults: 0 });
    setShowSearchResults(false);
  } finally {
    setSearchLoading(false);
  }
}, 300); // 300ms delay

const handleSearchChange = (e) => {
  const query = e.target.value;
  setSearchQuery(query);
  
  if (query.trim().length >= 2) {
    setSearchLoading(true);
    performSearch(query);
  } else {
    setSearchResults({ patients: [], doctors: [], totalResults: 0 });
    setShowSearchResults(false);
    setSearchLoading(false);
  }
};

const handleSearchSubmit = (e) => {
  e.preventDefault();
  if (searchQuery.trim()) {
    console.log('🔍 Search submitted:', searchQuery);
    // You can add additional submit logic here if needed
  }
};


const handleResultClick = (result, type) => {
  console.log(`🎯 Selected ${type}:`, result);
  
  // Navigate based on result type
  if (type === 'patient') {
    navigate(`/doctor/patients/${result._id || result.id}`);
  } else if (type === 'doctor') {
    // You can implement doctor profile navigation here
    console.log('Navigate to doctor profile:', result);
  }
  
  // Close search
  setSearchOpen(false);
  setSearchQuery('');
  setShowSearchResults(false);
};


  // ✅ Existing state
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalAppointments: 0,
    pendingEntries: 0,
    newMessages: 0
  });
  const [, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('This Week');
  const [doctorInfo, setDoctorInfo] = useState({
    firstName: '',
    lastName: ''
  });
  
  // ✅ NEW: Profile picture state (same as layout)
  const [currentUser, setCurrentUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  
  // ✅ NEW: User state for DoctorStatusGuard
  const [user, setUser] = useState(null);
  
  // ✅ ENHANCED: Changed to store ALL appointments, not just today's
  const [allAppointments, setAllAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  
  // ✅ NEW: Slider state for appointments
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // ✅ NEW: Modal state for appointment details
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  
  // ✅ Calendar state
  const [calendarState, setCalendarState] = useState({
    currentDate: new Date(),
    viewMode: 'week',
    selectedDate: new Date()
  });
  
  const navigate = useNavigate();

  // ✅ NEW: Enhanced profile loading function with user state for StatusGuard
  const loadDoctorInfo = async () => {
    try {
      setProfileLoading(true);
      
      // First, get user data from storage (like the layout does)
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        const userObj = JSON.parse(userData);
        setCurrentUser(userObj);
        setUser(userObj); // ✅ NEW: Set user for StatusGuard
        
        // Update doctorInfo state with initial data
        setDoctorInfo({
          firstName: userObj.firstName || 'Doctor',
          lastName: userObj.lastName || ''
        });
      }
      
      // Then fetch fresh profile data from API
      try {
        const response = await doctorService.getProfile();
        let profileData = {};
        
        if (response.success && response.data) {
          profileData = response.data;
        } else if (response.data) {
          profileData = response.data;
        } else {
          profileData = response;
        }
        
        // Update both currentUser and doctorInfo states
        const updatedUser = {
          ...currentUser,
          ...profileData,
          id: currentUser?.id || currentUser?._id || profileData._id,
          role: currentUser?.role || profileData.role || 'doctor'
        };
        
        setCurrentUser(updatedUser);
        setUser(updatedUser); // ✅ NEW: Update user for StatusGuard
        
        setDoctorInfo({
          firstName: profileData.firstName || 'Doctor',
          lastName: profileData.lastName || ''
        });
        
        console.log('👨‍⚕️ [Dashboard Profile] Doctor info loaded:', profileData.firstName);
        console.log('🔒 [StatusGuard] User status:', {
          accountStatus: updatedUser.accountStatus,
          verificationStatus: updatedUser.verificationStatus,
          role: updatedUser.role
        });
        
      } catch (apiError) {
        console.warn('⚠️ [Dashboard Profile] Could not fetch fresh profile data:', apiError.message);
        // Keep the data from localStorage if API fails
      }
      
    } catch (error) {
      console.error('❌ [Dashboard Profile] Error loading doctor info:', error);
      // Fallback values
      setDoctorInfo({ firstName: 'Doctor', lastName: '' });
    } finally {
      setProfileLoading(false);
    }
  };

  // ✅ NEW: Profile picture display helper (same as layout)
  const getDoctorProfileDisplay = () => {
    if (profileLoading) {
      return (
        <div className="doctor-profile-loading">
          <div className="profile-loading-spinner"></div>
        </div>
      );
    }
    
    if (currentUser?.profilePicture) {
      return (
        <>
          <img 
            src={currentUser.profilePicture} 
            alt="Profile" 
            className="doctor-profile-image"
            onError={(e) => {
              console.warn('❌ [Dashboard Profile] Image failed to load:', e.target.src);
              e.target.style.display = 'none';
              const initialsDiv = e.target.parentNode.querySelector('.doctor-profile-initials-fallback');
              if (initialsDiv) {
                initialsDiv.style.display = 'flex';
              }
            }}
            onLoad={() => {
              console.log('✅ [Dashboard Profile] Image loaded successfully');
            }}
          />
          <div className="doctor-profile-initials-fallback" style={{ display: 'none' }}>
            {(currentUser?.firstName || 'D').charAt(0)}{(currentUser?.lastName || 'R').charAt(0)}
          </div>
        </>
      );
    } else {
      const firstName = currentUser?.firstName || doctorInfo.firstName || 'D';
      const lastName = currentUser?.lastName || doctorInfo.lastName || 'R';
      return (
        <div className="doctor-profile-initials">
          {firstName.charAt(0)}{lastName.charAt(0)}
        </div>
      );
    }
  };

  // Enhanced useEffect for search
useEffect(() => {
  if (searchOpen) {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEscapeKey);
  };
}, [searchOpen]);


  // ✅ NEW: Slider navigation functions
  const goToPrevious = () => {
    setCurrentSlide(prev => 
      prev === 0 ? allAppointments.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentSlide(prev => 
      prev === allAppointments.length - 1 ? 0 : prev + 1
    );
  };

  // ✅ NEW: Reset slider when appointments change
useEffect(() => {
  setCurrentSlide(0);
}, [allAppointments.length]);


const handleClickOutside = (event) => {
  if (searchOpen && !event.target.closest('.dashboard-search-container')) {
    setSearchOpen(false);
    setSearchQuery('');
    setShowSearchResults(false);
  }
};

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && searchOpen) {
    setSearchOpen(false);
    setSearchQuery('');
    setShowSearchResults(false);
  }
};

useEffect(() => {
  const handleClickOutside = (event) => {
    if (searchOpen && !event.target.closest('.dashboard-search-container')) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && searchOpen) {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  if (searchOpen) {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEscapeKey);
  };
}, [searchOpen]);

  // ✅ NEW: Modal handler functions
  const handleViewDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const closeAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  // ✅ Enhanced function to handle joining video meeting with feature check
  const handleJoinMeeting = (appointment) => {
    // Check if Care/Report feature is enabled (appointments functionality)
    if (!featureControl.isFeatureEnabled('Care / Report')) {
      alert('Video meeting functionality is currently disabled for your clinic. Please contact your administrator.');
      return;
    }

    const meetingLink = appointment.meetingLink;
    
    if (!meetingLink) {
      alert('No meeting link available for this appointment. Please ensure the appointment is confirmed and has a video session scheduled.');
      return;
    }
    
    console.log('🎥 Doctor joining meeting:', meetingLink);
    
    // Determine meeting platform
    const getMeetingPlatform = (url) => {
      try {
        const urlObj = new URL(url);
        if (urlObj.hostname === 'meet.google.com') return 'Google Meet';
        if (urlObj.hostname === 'meet.jit.si') return 'Jitsi Meet';
        if (urlObj.hostname.includes('zoom')) return 'Zoom';
        return 'Video Conference';
      } catch (e) {
        return 'Video Conference';
      }
    };
    
    const platform = getMeetingPlatform(meetingLink);
    
    // Show confirmation dialog with tenant branding
    const confirmed = window.confirm(
      `Join ${platform} session with ${appointment.patientName}?\n\n` +
      `${platformName} - ${currentTenant?.name || 'Clinic'}\n` +
      `Session Platform: ${platform}\n` +
      `Patient: ${appointment.patientName}\n` +
      `Time: ${appointment.time}\n\n` +
      `This will open the video conference in a new window.`
    );
    
    if (confirmed) {
      // Open meeting in new window/tab
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
      
      // Show success message with tenant branding
      setTimeout(() => {
        alert(
          `${platform} should now be opening in a new window.\n\n` +
          `📋 ${platformName} Session Tips:\n` +
          `• Ensure good internet connection\n` +
          `• Test your camera and microphone\n` +
          `• Both you and the patient will join the same room\n` +
          `• No account registration required for Jitsi Meet`
        );
      }, 1000);
    }
  };

  // ✅ Keep existing functions unchanged
  const isSessionActive = (appointment) => {
    if (!appointment.appointmentDate) return false;
    
    const appointmentTime = new Date(appointment.appointmentDate);
    const now = new Date();
    const diffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    return diffMinutes <= 15 && diffMinutes >= -120;
  };

  const getSessionStatus = (appointment) => {
    if (!appointment.meetingLink) return { status: 'No Meeting', color: '#666' };
    if (!appointment.appointmentDate) return { status: 'Scheduled', color: theme?.primaryColor || '#4CAF50' };
    
    const appointmentTime = new Date(appointment.appointmentDate);
    const now = new Date();
    const diffMinutes = (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
    
    if (diffMinutes <= 5 && diffMinutes >= -60) {
      return { status: 'Active Now', color: '#FF4444' };
    } else if (diffMinutes <= 15 && diffMinutes > 5) {
      return { status: 'Starting Soon', color: '#FF9800' };
    } else if (diffMinutes > 15) {
      return { status: 'Scheduled', color: theme?.primaryColor || '#4CAF50' };
    } else {
      return { status: 'Ended', color: '#666' };
    }
  };

const processAllAppointments = (allAppointments) => {
  const today = new Date();
  // ✅ FIX: Get today's date in local timezone
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  console.log('🔍 Processing appointments - Total received:', allAppointments.length);
  console.log('🔍 Today\'s date (local):', todayStr);
  
  // ✅ FILTER: Only today's appointments that are NOT completed/cancelled/declined
  const activeTodayAppointments = allAppointments.filter(appt => {
    if (!appt.appointmentDate) return false;
    
    // ✅ FIX: Extract date directly from ISO string (no timezone conversion)
    const apptDateStr = appt.appointmentDate.substring(0, 10); // Gets "YYYY-MM-DD"
    const isToday = apptDateStr === todayStr;
    
    console.log(`  Appointment: ${appt.patient?.firstName} - Date: ${apptDateStr}, Is Today: ${isToday}, Status: ${appt.status}`);
    
    const activeStatuses = ['Pending', 'Scheduled'];
    const isActive = activeStatuses.includes(appt.status);
    
    return isToday && isActive;
  });
  
  console.log(`✅ Filtered to ${activeTodayAppointments.length} active appointments for today`);
  
  // ✅ FIX: Extract time without timezone conversion
  const extractTimeFromISO = (isoString) => {
    if (!isoString) return 'Time TBD';
    
    const match = isoString.match(/T(\d{2}):(\d{2})/);
    if (!match) return 'Time TBD';
    
    const hours = parseInt(match[1]);
    const minutes = match[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    
    return `${hour12}:${minutes} ${ampm}`;
  };
  
  // Transform filtered appointments to consistent format
  const processedAppointments = activeTodayAppointments.map(appt => ({
    id: appt._id,
    patient: appt.patient,
    patientName: appt.patient ? 
      `${appt.patient.firstName || ''} ${appt.patient.lastName || ''}`.trim() :
      'Unknown Patient',
    patientType: "Patient",
    appointmentDate: appt.appointmentDate,
    time: extractTimeFromISO(appt.appointmentDate),
    date: appt.appointmentDate ? new Date(appt.appointmentDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : 'Date TBD',
    meetingLink: appt.meetingLink,
    status: appt.status,
    meetingType: appt.meetingType || 'jitsi',
    duration: appt.duration || 30,
    appointmentType: appt.appointmentType || 'Consultation',
    rawDate: appt.appointmentDate ? new Date(appt.appointmentDate) : new Date(),
    isToday: true,
    isPast: false,
    isFuture: false
  }));

  // Sort by time (earliest first)
  const sortedAppointments = processedAppointments.sort((a, b) => 
    a.rawDate.getTime() - b.rawDate.getTime()
  );

  console.log('✅ Today\'s active appointments:', sortedAppointments.length);
  
  // For calendar, also fix the time extraction
  const allProcessed = allAppointments.map(appt => ({
    appointmentDate: appt.appointmentDate,
    patientName: appt.patient ? 
      `${appt.patient.firstName || ''} ${appt.patient.lastName || ''}`.trim() :
      'Unknown Patient',
    time: extractTimeFromISO(appt.appointmentDate),
  }));
  
  return { 
    allSorted: sortedAppointments,
    todaysOnly: sortedAppointments,
    allForCalendar: allProcessed
  };
};
  // ✅ ENHANCED: Helper function to get appointment status badge
  const getAppointmentStatusBadge = (appointment) => {
    if (appointment.isToday) {
      if (isSessionActive(appointment)) {
        return { text: 'Active Now', className: 'status-active' };
      } else {
        return { text: 'Today', className: 'status-today' };
      }
    } else if (appointment.isPast) {
      return { text: 'Past', className: 'status-past' };
    } else {
      return { text: 'Upcoming', className: 'status-upcoming' };
    }
  };

  // ✅ ENHANCED: useEffect with proper profile picture integration
  useEffect(() => {
    // Enhanced: Fetch dashboard data with feature-based filtering
    const fetchDashboardData = async () => {
      try {
        console.log('🔍 Fetching dashboard data with feature control...');
        
        // ✅ Enhanced: Fetch doctor profile information with picture
        await loadDoctorInfo();
        
        // Enhanced: Fetch appointments only if Care/Report feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Care / Report')) {
          try {
            console.log('🔍 Fetching ALL appointments (feature enabled)...');
            const appointmentsResponse = await doctorService.getAppointments();
            
            let appointments = [];
            if (appointmentsResponse.success && appointmentsResponse.data) {
              appointments = appointmentsResponse.data;
            } else if (Array.isArray(appointmentsResponse)) {
              appointments = appointmentsResponse;
            } else if (appointmentsResponse.data && Array.isArray(appointmentsResponse.data)) {
              appointments = appointmentsResponse.data;
            }
            
            console.log('✅ Fetched appointments:', appointments.length);

              // ✅ FIXED: Process appointments with separate calendar data
              const { allSorted, todaysOnly, allForCalendar } = processAllAppointments(appointments);

              console.log('📊 APPOINTMENT BREAKDOWN:');
              console.log('  - Total fetched:', appointments.length);
              console.log('  - Today active (slider):', allSorted.length);
              console.log('  - Calendar appointments:', allForCalendar.length);
              console.log('📅 Sample of allSorted appointments:', allSorted.slice(0, 3).map(a => ({
                patient: a.patientName,
                date: a.date,
                isToday: a.isToday,
                status: a.status
              })));

              setAllAppointments(allSorted);
              setTodayAppointments(todaysOnly);
              setCalendarAppointments(allForCalendar);
            
            setStats(prevStats => ({
              ...prevStats,
              totalAppointments: appointments.length
            }));
            
          } catch (appointmentsError) {
            console.error('❌ Error fetching appointments:', appointmentsError);
            setAllAppointments([]);
            setTodayAppointments([]);
          }
        } else {
          console.log('⚠️ Appointments feature disabled - skipping fetch');
          setAllAppointments([]);
          setTodayAppointments([]);
          setStats(prevStats => ({ ...prevStats, totalAppointments: 0 }));
        }
        
        // Enhanced: Fetch patients only if User Profiles feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('User Profiles')) {
          try {
            const patientsResponse = await doctorService.getPatients();
            const patients = patientsResponse.data || patientsResponse.patients || [];
            
            setStats(prevStats => ({
              ...prevStats,
              totalPatients: patients.length
            }));
            
            console.log('✅ Patients loaded:', patients.length);
          } catch (patientsError) {
            console.error('❌ Error fetching patients:', patientsError);
            setStats(prevStats => ({ ...prevStats, totalPatients: 0 }));
          }
        } else {
          console.log('⚠️ User Profiles feature disabled - skipping patients fetch');
          setStats(prevStats => ({ ...prevStats, totalPatients: 0 }));
        }
        
        // Enhanced: Fetch journal entries only if Journal Entries feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Journal Entries')) {
          try {
            const entriesResponse = await doctorService.getJournalEntries({
              analyzed: 'unanalyzed',
              limit: 5
            });
            const entries = entriesResponse.data || [];
            
            setRecentEntries(entries);
            setStats(prevStats => ({
              ...prevStats,
              pendingEntries: entries.length
            }));
            
            console.log('✅ Journal entries loaded:', entries.length);
          } catch (entriesError) {
            console.error('❌ Error fetching journal entries:', entriesError);
            setStats(prevStats => ({ ...prevStats, pendingEntries: 0 }));
          }
        } else {
          console.log('⚠️ Journal Entries feature disabled - skipping fetch');
          setStats(prevStats => ({ ...prevStats, pendingEntries: 0 }));
        }
        
        // Enhanced: Messages only if Notifications feature is enabled
        if (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Notifications')) {
          // Implement message fetching when ready
          setStats(prevStats => ({ ...prevStats, newMessages: 0 }));
        } else {
          setStats(prevStats => ({ ...prevStats, newMessages: 0 }));
        }
        
        setLoading(false);
        console.log('✅ Dashboard data loading complete with feature control');
        
      } catch (error) {
        console.error('❌ Error in fetchDashboardData:', error);
        setLoading(false);
        
        setDoctorInfo({ firstName: 'Doctor', lastName: '' });
        setAllAppointments([]);
        setTodayAppointments([]);
        setStats({
          totalPatients: 0,
          totalAppointments: 0,
          pendingEntries: 0,
          newMessages: 0
        });
      }
    };
    
    // Critical FIX: Only fetch data once when component mounts
    if (featureControl.isFeatureEnabled) {
      fetchDashboardData();
    }
  }, []); // Empty dependency array - runs only once!

  // ✅ Calendar helper functions
  const getMonthName = (date) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[date.getMonth()];
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const goToPreviousPeriod = () => {
    setCalendarState(prevState => {
      const newDate = new Date(prevState.currentDate);
      if (prevState.viewMode === 'week') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
      return { ...prevState, currentDate: newDate };
    });
  };

  const goToNextPeriod = () => {
    setCalendarState(prevState => {
      const newDate = new Date(prevState.currentDate);
      if (prevState.viewMode === 'week') {
        newDate.setDate(newDate.getDate() + 7);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return { ...prevState, currentDate: newDate };
    });
  };

  const toggleViewMode = () => {
    setCalendarState(prevState => ({
      ...prevState,
      viewMode: prevState.viewMode === 'week' ? 'month' : 'week'
    }));
  };

  const getWeekDays = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day;
    const weekDays = [];
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(date);
      currentDay.setDate(diff + i);
      weekDays.push({
        date: currentDay,
        name: daysOfWeek[i],
        number: currentDay.getDate(),
        month: currentDay.getMonth(),
        year: currentDay.getFullYear()
      });
    }
    return weekDays;
  };

  

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const totalDays = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const daysArray = [];
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const dayNumber = prevMonthDays - i;
      daysArray.push({
        date: new Date(year, month - 1, dayNumber),
        name: daysOfWeek[(7 + dayNumber - i) % 7],
        number: dayNumber,
        month: month - 1,
        year: year,
        isCurrentMonth: false
      });
    }
    
    for (let i = 1; i <= totalDays; i++) {
      const dayDate = new Date(year, month, i);
      daysArray.push({
        date: dayDate,
        name: daysOfWeek[dayDate.getDay()],
        number: i,
        month: month,
        year: year,
        isCurrentMonth: true
      });
    }
    
    const remainingDays = 42 - daysArray.length;
    for (let i = 1; i <= remainingDays; i++) {
      const dayDate = new Date(year, month + 1, i);
      daysArray.push({
        date: dayDate,
        name: daysOfWeek[dayDate.getDay()],
        number: i,
        month: month + 1,
        year: year,
        isCurrentMonth: false
      });
    }
    
    return daysArray;
  };

  const hasAppointments = (date) => {
  const dateStr = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  return calendarAppointments.some(appt => { // ✅ CHANGED: Use calendarAppointments
    if (!appt.appointmentDate) return false;
    const apptDate = new Date(appt.appointmentDate);
    const apptDateStr = `${apptDate.getFullYear()}-${apptDate.getMonth() + 1}-${apptDate.getDate()}`;
    return apptDateStr === dateStr;
  });
};

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const renderCalendar = () => {
    const { currentDate, viewMode } = calendarState;
    const isMonthView = viewMode === 'month';
    const days = isMonthView ? getMonthDays(currentDate) : getWeekDays(currentDate);
    const monthYear = `${getMonthName(currentDate)} ${currentDate.getFullYear()}`;
    
    return (
      <DoctorStatusGuard 
        user={user} 
        requiredStatus="approved"
        fallback={
          <div className="weekly-calendar">
            <RestrictedFeatureMessage 
              title="Calendar & Appointments"
              message="Calendar access available after verification"
            />
          </div>
        }
      >
        <FeatureWrapper feature="Care / Report" showMessage={true}>
          <div className="weekly-calendar">
            <div className="calendar-month-header">
              <div className="month-title">{monthYear}</div>
              <div className="month-navigation">
                <button 
                  className="month-nav-button view-toggle" 
                  onClick={toggleViewMode}
                >
                  {isMonthView ? 'Week View' : 'Month View'}
                </button>
                <button className="month-nav-button prev" onClick={goToPreviousPeriod}>&#10094;</button>
                <button className="month-nav-button next" onClick={goToNextPeriod}>&#10095;</button>
              </div>
            </div>
            
            {isMonthView ? (
              <div className="month-grid">
                <div className="day-names-row">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, index) => (
                    <div key={`header-${index}`} className="day-name-cell">{dayName}</div>
                  ))}
                </div>
                <div className="month-days-grid">
                  {days.map((day, index) => (
                    <div 
                      key={`day-${index}`} 
                      className={`month-day ${!day.isCurrentMonth ? 'other-month' : ''} 
                                  ${isToday(day.date) ? 'today' : ''} 
                                  ${hasAppointments(day.date) ? 'has-appointment' : ''}`}
                    >
                      <div className="month-day-number">{day.number}</div>
                      {hasAppointments(day.date) && (
                        <div className="month-day-indicators">
                          <div className="day-appointment-dot"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="week-row">
                {days.map((day, index) => (
                  <div 
                    key={`week-day-${index}`} 
                    className={`week-day ${hasAppointments(day.date) ? 'has-appointment' : ''} 
                                ${isToday(day.date) ? 'today' : ''}`}
                  >
                    <div className="week-day-header">
                      <div className="day-name">{day.name}</div>
                      <div className="day-number">{day.number}</div>
                    </div>
                    <div className="day-event-container">
                        {hasAppointments(day.date) && (
                            <>
                              {/* ✅ SHOW ACTUAL APPOINTMENT DETAILS */}
                              {calendarAppointments 
                                .filter(appt => {
                                if (!appt.appointmentDate) return false;
                                const apptDate = new Date(appt.appointmentDate);
                                const dayStr = `${day.date.getFullYear()}-${day.date.getMonth() + 1}-${day.date.getDate()}`;
                                const apptStr = `${apptDate.getFullYear()}-${apptDate.getMonth() + 1}-${apptDate.getDate()}`;
                                return apptStr === dayStr;
                              })
                              .map((appt, index) => (
                                <div key={index} className="appointment-indicator">
                                  <div className="appointment-time">{appt.time}</div>
                                  <div className="appointment-patient">{appt.patientName}</div>
                                </div>
                              ))
                            }
                          </>
                        )}
                      </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FeatureWrapper>
      </DoctorStatusGuard>
    );
  };

  // ✅ NEW: Appointment Slider Component - Show 1 appointment at a time
  const renderAppointmentSlider = () => {
    // ✅ Get current appointment
    const currentAppointment = allAppointments[currentSlide];
    
    return (
      <>
        <DoctorStatusGuard 
          user={user} 
          requiredStatus="approved"
          fallback={
            <div className="doctor-dashboard-appointment-slider-container">
              <RestrictedFeatureMessage 
                title="Patient Appointments"
                message="Appointment management available after verification"
              />
            </div>
          }
        >
          <FeatureWrapper feature="Care / Report" showMessage={true}>
            <div className="doctor-dashboard-appointment-slider-container">
              <div className="slider-header">
                <h3 className="doc-appointments-card-section-title">
                  Today’s Appointments
                </h3>
                
                {/* ✅ Navigation Arrows */}
                {allAppointments.length > 1 && (
                  <div className="slider-navigation">
                    <button 
                      className="slider-nav-button prev"
                      onClick={goToPrevious}
                    >
                      <span className="nav-arrow">‹</span>
                    </button>
                    <button 
                      className="slider-nav-button next"
                      onClick={goToNext}
                    >
                      <span className="nav-arrow">›</span>
                    </button>
                  </div>
                )}
              </div>
              
              {allAppointments.length === 0 ? (
                <div className="no-appointments-slider">
                  <p>🗓️ No appointments scheduled</p>
                  <p className="no-appointments-subtitle">Create new appointments to get started!</p>
                </div>
              ) : (
                <div className="appointment-slider-content">
                  {/* ✅ Single Appointment Card */}
                  <div className={`appointment-card-slider ${currentAppointment.isToday ? 'is-today' : ''} ${currentAppointment.isPast ? 'is-past' : ''}`}>
                    {/* Status Badge */}

                    
                    {/* Main Content */}
                    <div className="appointment-main-content">
                      {/* Patient Section */}
                      <div className="slider-patient-section">
  <div className="patient-avatar-large">
    {currentAppointment.patient?.profilePicture && 
     currentAppointment.patient.profilePicture !== 'default-profile.png' ? (
      <img 
        src={currentAppointment.patient.profilePicture} 
        alt="Patient Profile" 
        className="patient-profile-image-large"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextElementSibling.style.display = 'flex';
        }}
      />
    ) : null}
    <div 
      className="patient-initials-large"
      style={{
        display: currentAppointment.patient?.profilePicture && 
                currentAppointment.patient.profilePicture !== 'default-profile.png' 
                ? 'none' : 'flex'
      }}
    >
      {currentAppointment.patientName.charAt(0).toUpperCase()}
    </div>
  </div>
  <div className="patient-info-large">
    <h2 className="patient-name-large">{currentAppointment.patientName}</h2>
    <p className="patient-type-large">Patient • {currentAppointment.appointmentType}</p>
  </div>
</div>
                      
                      {/* DateTime Section */}
                      <div className="slider-datetime-section">
                        <div className="datetime-row">
                          <div className="datetime-item-large">
                            <span className="datetime-icon-large"></span>
                            <div className="datetime-text">
                              <span className="doc-appointment-card-datetime-label">Date:</span>
                              <span className="datetime-value-large">
                                {currentAppointment.isToday ? 'Today' : currentAppointment.date}
                              </span>
                            </div>
                          </div>
                          
                          <div className="datetime-item-large">
                            <span className="datetime-icon-large"></span>
                            <div className="datetime-text">
                              <span className="doc-appointment-card-datetime-label">Time:</span>
                              <span className="datetime-value-large">{currentAppointment.time}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Video Session Indicator */}
                      {currentAppointment.meetingLink && (
                        <div className="video-session-indicator">
                          <span className="video-text">Video Session Available</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="slider-actions">
                      <button 
                        className={`slider-action-button primary ${currentAppointment.isToday ? 'today-session' : ''}`}
                        onClick={() => handleViewDetails(currentAppointment)}
                      >
                        {currentAppointment.isToday && currentAppointment.meetingLink ? 
                          'View Details' : 
                          'View Details'
                        }
                      </button>
                      
                      {currentAppointment.meetingLink && !currentAppointment.isToday && (
                        <button 
                          className="slider-action-button secondary"
                          onClick={() => handleJoinMeeting(currentAppointment)}
                        >
                          Preview Meeting
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* ✅ Slide Indicators (Dots) */}
                  {allAppointments.length > 1 && (
                    <div className="slide-indicators">
                      {allAppointments.map((_, index) => (
                        <button
                          key={index}
                          className={`slide-dot ${index === currentSlide ? 'active' : ''} ${allAppointments[index].isToday ? 'today-dot' : ''}`}
                          onClick={() => setCurrentSlide(index)}
                          title={`${allAppointments[index].patientName} - ${allAppointments[index].isToday ? 'Today' : allAppointments[index].date}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              
            </div>
          </FeatureWrapper>
        </DoctorStatusGuard>

        {/* ✅ Enhanced: Appointment Details Modal */}
        {showAppointmentModal && selectedAppointment && (
          <div className="modal-overlay-fixed" onClick={closeAppointmentModal}>
            <div className="modal-content-fixed" onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div className="modal-header-fixed">
                <h2 className="modal-title-fixed">Appointment Details</h2>
                <div className={`modal-status-badge ${getAppointmentStatusBadge(selectedAppointment).className}`}>
                  {getAppointmentStatusBadge(selectedAppointment).text}
                </div>
                <button 
                  className="modal-close-fixed"
                  onClick={closeAppointmentModal}
                >
                  ×
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="modal-body-fixed">
                {/* Patient Information */}
                <div className="modal-patient-section">
                  <div className="modal-patient-avatar">
  {selectedAppointment.patient?.profilePicture && 
   selectedAppointment.patient.profilePicture !== 'default-profile.png' ? (
    <img 
      src={selectedAppointment.patient.profilePicture} 
      alt="Patient Profile" 
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: '50%'
      }}
      onError={(e) => {
        e.target.style.display = 'none';
        const fallback = e.target.parentElement.querySelector('.modal-patient-initials-fallback');
        if (fallback) fallback.style.display = 'flex';
      }}
    />
  ) : null}
  <div 
    className="modal-patient-initials-fallback"
    style={{
      display: selectedAppointment.patient?.profilePicture && 
              selectedAppointment.patient.profilePicture !== 'default-profile.png' 
              ? 'none' : 'flex',
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: '600',
      color: 'white'
    }}
  >
    {selectedAppointment.patientName.charAt(0).toUpperCase()}
  </div>
</div>
                  <div>
                    <h3 className="modal-patient-name">{selectedAppointment.patientName}</h3>
                    <p className="modal-patient-type">Patient • {selectedAppointment.appointmentType}</p>
                  </div>
                </div>
                
                {/* Enhanced: Date & Time Display */}
                <div className="modal-datetime-section">
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">📅</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Date</span>
                      <span className="datetime-value">
                        {selectedAppointment.isToday ? 'Today' : selectedAppointment.date}
                      </span>
                    </div>
                  </div>
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">🕛</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Time</span>
                      <span className="datetime-value">{selectedAppointment.time}</span>
                    </div>
                  </div>
                  <div className="modal-datetime-item">
                    <span className="datetime-icon">⏱️</span>
                    <div className="datetime-info">
                      <span className="datetime-label">Duration</span>
                      <span className="datetime-value">{selectedAppointment.duration} min</span>
                    </div>
                  </div>
                </div>
                
                {/* Meeting Section */}
                <div className="modal-meeting-section">
                  {/* Join Meeting Button or No Meeting Message */}
                  {selectedAppointment.meetingLink ? (
                    <button 
                      className={`modal-join-button ${isSessionActive(selectedAppointment) ? 'active' : ''} ${selectedAppointment.isToday ? 'today-session' : ''}`}
                      onClick={() => handleJoinMeeting(selectedAppointment)}
                    >
                      <span className="join-icon">📹</span>
                      {selectedAppointment.isToday ? 
                        (isSessionActive(selectedAppointment) ? 'Join Meeting Now' : 'Join Today\'s Session') :
                        'Join Video Session'
                      }
                    </button>
                  ) : (
                    <div className="modal-no-meeting">
                      <span className="no-meeting-icon">📹</span>
                      No meeting link available
                    </div>
                  )}
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="modal-footer-fixed">
                <button 
                  className="modal-close-button"
                  onClick={closeAppointmentModal}
                >
                  Close
                </button>
                {selectedAppointment.isToday && selectedAppointment.meetingLink && (
                  <button 
                    className="modal-join-footer-button"
                    onClick={() => handleJoinMeeting(selectedAppointment)}
                  >
                    📹 Join Session
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ✅ Enhanced: StatCard with feature-based styling and conditional rendering
  const StatCard = ({ title, value, subtitle, feature, onClick }) => {
    const isFeatureEnabled = feature ? (featureControl.isFeatureEnabled && featureControl.isFeatureEnabled(feature)) : true;
    
    if (feature && !isFeatureEnabled) {
      return (
        <div className="stat-card disabled" onClick={onClick}>
          <h3 className="stat-title">{title}</h3>
          <div className="stat-value">--</div>
          <div className="stat-subtitle">Feature Disabled</div>
        </div>
      );
    }
    
    return (
      <div className="stat-card" onClick={onClick}>
        <h3 className="stat-title">{title}</h3>
        <div className="stat-value">{value}</div>
        <div className="stat-subtitle">{subtitle}</div>
      </div>
    );
  };

  if (loading) {
  return (
    <div className="neurolex-loading-container">
      <div className="neurolex-loading-insideContainer">
      <div className="neurolex-dashboard-spinner">
        <div className="spinner-circle outer"></div>
        <div className="spinner-circle middle"></div>
        <div className="spinner-circle inner"></div>
        <div className="spinner-center">
          <img 
            src={neurolexLogo} 
            alt="NeuroLex Logo" 
            className="loading-logo"
            onError={(e) => {
              console.warn('❌ Logo failed to load from:', neurolexLogo);
              // Fallback to brain emoji if logo doesn't load
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) {
                fallback.style.display = 'flex';
                fallback.style.alignItems = 'center';
                fallback.style.justifyContent = 'center';
              }
            }}
            onLoad={() => {
              console.log('✅ NeuroLex logo loaded successfully:', neurolexLogo);
            }}
          />
          <div className="loading-logo-fallback" style={{ display: 'none' }}>
            🧠
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

  return (
    <div className="dashboard-wrapper">
      {/* ✅ NEW: Status Banner for pending doctors */}
      <StatusBanner user={user} />
      
      {/* ✅ Enhanced: Header with tenant branding and profile picture */}
      <div className="dashboard-header">
        <div className="welcome-message">
          <h1 className="welcome-title">
            Welcome back, Dr. {doctorInfo.firstName} {doctorInfo.lastName}.
          </h1>
          <p className="welcome-subtitle">
            {currentTenant?.name ? `${currentTenant.name} - ` : ''} Medical Dashboard. 
            Here's a gentle overview to guide your day.
          </p>
        </div>
        <div className="header-actions">
          {/* ✅ NEW: Always accessible profile completion card */}
          <DoctorStatusGuard user={user} requiredStatus="any">
            <div className="quick-access-card">
              <button 
                  className="profile-completion-button"
                  onClick={() => navigate('/doctor-onboarding')}
                  title="Complete your professional profile"
                >
                  <img 
                    src={profileIcon} 
                    alt="Profile Icon" 
                    className="profile-button-icon"
                  />
                  Complete Profile
                </button>
            </div>
          </DoctorStatusGuard>
          
         
       
  <FeatureWrapper feature="Config">
    <div className="dashboard-search-container">
      {/* Search Results Dropdown */}
     {showSearchResults && searchOpen && (
            <div className={`search-results-dropdown ${showSearchResults ? 'results-visible' : ''}`}>
              {searchLoading ? (
                <div className="search-loading">
                  <div className="search-loading-spinner"></div>
                  <span>Searching...</span>
                </div>
              ) : searchResults.totalResults === 0 ? (
                <div className="no-search-results">
                  <span>No results found for "{searchQuery}"</span>
                </div>
              ) : (
                <>
                  {/* Patients Section */}
                  {searchResults.patients.length > 0 && (
                    <div className="search-results-section">
                      <div className="search-section-header">
                        <span className="search-section-icon">👥</span>
                        <span className="search-section-title">Patients ({searchResults.patients.length})</span>
                      </div>
                      {searchResults.patients.map((patient, index) => (
                        <div
                          key={`patient-${patient._id || patient.id}-${index}`}
                          className="search-result-item"
                          onClick={() => handleResultClick(patient, 'patient')}
                        >
                          <div className="result-avatar patient-avatar-small">
                            {(patient.firstName || 'P').charAt(0).toUpperCase()}
                          </div>
                          <div className="result-details">
                            <div className="result-name">
                              {patient.firstName && patient.lastName ? 
                                `${patient.firstName} ${patient.lastName}` : 
                                patient.firstName || patient.lastName || 'Unknown Patient'}
                            </div>
                            <div className="result-meta">
                              {patient.email || 'No email'} • Patient
                            </div>
                          </div>
                          <div className="result-action">→</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Doctors Section */}
                  {searchResults.doctors.length > 0 && (
                    <div className="search-results-section">
                      <div className="search-section-header">
                        <span className="search-section-icon">👩‍⚕️</span>
                        <span className="search-section-title">Doctors ({searchResults.doctors.length})</span>
                      </div>
                      {searchResults.doctors.map((doctor, index) => (
                        <div
                          key={`doctor-${doctor._id || doctor.id}-${index}`}
                          className="search-result-item"
                          onClick={() => handleResultClick(doctor, 'doctor')}
                        >
                          <div className="result-avatar doctor-avatar-small">
                            {(doctor.firstName || 'D').charAt(0).toUpperCase()}
                          </div>
                          <div className="result-details">
                            <div className="result-name">
                              Dr. {doctor.firstName && doctor.lastName ? 
                                `${doctor.firstName} ${doctor.lastName}` : 
                                doctor.firstName || doctor.lastName || 'Unknown'}
                            </div>
                            <div className="result-meta">
                              {doctor.specialization || 'Doctor'} • {doctor.email || 'No email'}
                            </div>
                          </div>
                          <div className="result-action">→</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

      {/* Search Input */}
      {searchOpen && (
        <div className="dashboard-search-field-wrapper dashboard-search-open">
          <form className="dashboard-search-form" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search patients, doctors..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="dashboard-search-input"
              autoFocus
            />
            {searchLoading && (
              <div className={`search-input-loading ${searchLoading ? 'loading-visible' : ''}`}>
                <div className="input-spinner"></div>
              </div>
            )}
          </form>
        </div>
      )}
      
      {/* Search Toggle Button */}
      <button 
        type="button"
        onClick={toggleSearch}
        className={`dashboard-search-button ${searchOpen ? 'dashboard-search-active' : ''}`}
        title={searchOpen ? 'Close search' : 'Open search'}
      >
        <img 
          src={searchOpen ? closeIcon : searchIcon} 
          alt={searchOpen ? 'Close search' : 'Open search'}
          className="dashboard-search-icon"
        />
      </button>
</div>
  </FeatureWrapper>


          <FeatureWrapper feature="Notifications">
            <NotificationButton />
          </FeatureWrapper>
          <div className="user-avatar">
            <div className={`avatar-placeholder ${profileLoading ? 'loading' : ''}`}>
              {getDoctorProfileDisplay()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Divider above Patient and Appointment Overview */}
      <div className="section-divider overview-divider"></div>

      {/* ✅ Enhanced: Overview section with feature-based stats and status guard */}
      <div className="overview-section">
        <div className="section-header">
          <h2 className="doc-dashboard-section-title">
            Patient and Appointment Overview
          </h2>
          <div className="timeframe-selector">
            <select 
              value={selectedTimeframe} 
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="timeframe-select"
            >
              <option value="This Week">This Week</option>
              <option value="This Month">This Month</option>
              <option value="This Year">This Year</option>
            </select>
          </div>
        </div>
        
        <div className="stats-container">
          {/* ✅ RESTRICTED: Total Patients */}
  <DoctorStatusGuard 
    user={user} 
    requiredStatus="approved"
    fallback={
      <div className="stat-card disabled">
        <h3 className="stat-title">Total Patients</h3>
        <div className="stat-value">
          <img src={restrictedIcon} alt="Restricted" className="restricted-icon-stat" />
        </div>
        <div className="stat-subtitle">Available After Verification</div>
      </div>
    }
  >
    <StatCard
      title="Total Patients"
      value={stats.totalPatients}
      subtitle="Patients in Your Care"
      feature="User Profiles"
      onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('User Profiles') && navigate('/doctor/patients')}
    />
  </DoctorStatusGuard>

          {/* ✅ RESTRICTED: Total Appointments */}
  <DoctorStatusGuard 
    user={user} 
    requiredStatus="approved"
    fallback={
      <div className="stat-card disabled">
        <h3 className="stat-title">Total Appointments</h3>
        <div className="stat-value">
          <img src={restrictedIcon} alt="Restricted" className="restricted-icon-stat" />
        </div>
        <div className="stat-subtitle">Available After Verification</div>
      </div>
    }
  >
    <StatCard
      title="Total Appointments"
      value={stats.totalAppointments}
      subtitle="All Scheduled Sessions"
      feature="Care / Report"
      onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Care / Report') && navigate('/doctor/appointments')}
    />
  </DoctorStatusGuard>

  {/* ✅ RESTRICTED: Pending Review */}
  <DoctorStatusGuard 
    user={user} 
    requiredStatus="approved"
    fallback={
      <div className="stat-card disabled">
        <h3 className="stat-title">Pending Review</h3>
        <div className="stat-value">
          <img src={restrictedIcon} alt="Restricted" className="restricted-icon-stat" />
        </div>
        <div className="stat-subtitle">Available After Verification</div>
      </div>
    }
  >
    <StatCard
      title="Pending Review"
      value={stats.pendingEntries}
      subtitle="Journal Entries to Review"
      feature="Journal Entries"
      onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Journal Entries') && navigate('/doctor/journal-entries')}
    />
  </DoctorStatusGuard>

  {/* ✅ RESTRICTED: Messages */}
  <DoctorStatusGuard 
    user={user} 
    requiredStatus="approved"
    fallback={
      <div className="stat-card disabled">
        <h3 className="stat-title">Messages</h3>
        <div className="stat-value">
          <img src={restrictedIcon} alt="Restricted" className="restricted-icon-stat" />
        </div>
        <div className="stat-subtitle">Available After Verification</div>
      </div>
    }
  >
    <StatCard
      title="Messages"
      value={stats.newMessages}
      subtitle="New Messages"
      feature="Notifications"
      onClick={() => featureControl.isFeatureEnabled && featureControl.isFeatureEnabled('Notifications') && navigate('/doctor/messages')}
    />
  </DoctorStatusGuard>
        </div>
      </div>
      
      {/* Divider above calendar and appointments */}
      <div className="section-divider calendar-divider"></div>

      {/* ✅ Enhanced: Main content with feature wrappers and status guards */}
      <div className="main-content">
        <div className="calendar-section">
          {renderCalendar()}
        </div>
        <div className="appointments-section">
          {renderAppointmentSlider()}
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;